import React, { useState } from 'react';
import { tx, tr } from '../utils/i18n';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Mail, MapPin, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t, currentLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    budget: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        language: currentLanguage
      };

      const response = await axios.post(`${API}/contact`, payload);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          role: '',
          budget: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      if (error.response?.status === 400 && error.response?.data?.errors) {
        // Handle validation errors
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).join(', ');
        toast.error(`${error.response.data.message}: ${errorMessages}`);
      } else if (error.response?.status === 429) {
        // Handle rate limiting
        toast.error(error.response.data.message || 'Trop de demandes. Veuillez réessayer plus tard.');
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.message || 
          (tx(currentLanguage, 'Une erreur est survenue. Veuillez réessayer.', 'An error occurred. Please try again.', 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'));
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.firstName && 
           formData.lastName && 
           formData.email && 
           formData.company && 
           formData.budget;
  };

  const getBudgetOptions = () => {
    if (currentLanguage === 'en') {
      return [
        { value: "", label: t('selectBudget') },
        { value: "under-10k", label: "Under $10,000" },
        { value: "10k-50k", label: "$10,000 - $50,000" },
        { value: "50k-100k", label: "$50,000 - $100,000" },
        { value: "100k-500k", label: "$100,000 - $500,000" },
        { value: "over-500k", label: "Over $500,000" }
      ];
    } else if (currentLanguage === 'de') {
      return [
        { value: "", label: t('selectBudget') },
        { value: "under-10k", label: "Unter 10.000€" },
        { value: "10k-50k", label: "10.000€ - 50.000€" },
        { value: "50k-100k", label: "50.000€ - 100.000€" },
        { value: "100k-500k", label: "100.000€ - 500.000€" },
        { value: "over-500k", label: "Über 500.000€" }
      ];
    }
    return [
      { value: "", label: t('selectBudget') },
      { value: "under-10k", label: tr(currentLanguage, "Moins de 10 000€") },
      { value: "10k-50k", label: "10 000€ - 50 000€" },
      { value: "50k-100k", label: "50 000€ - 100 000€" },
      { value: "100k-500k", label: "100 000€ - 500 000€" },
      { value: "over-500k", label: tr(currentLanguage, "Plus de 500 000€") }
    ];
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('contactTitle')}
            <span className="block bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mt-2">
              {t('contactSubtitle')}
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('contactDescription')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('stayConnected')}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8">
                  {t('contactInfo')}
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <Card className="border-0 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                        <Mail className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{t('email')}</div>
                        <div className="text-gray-600">contact@optimhub.com</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{t('address')}</div>
                        <div className="text-gray-600">Paris, France</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Response Time */}
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl p-6 text-white">
                <h4 className="font-semibold mb-2">{t('guaranteedResponse')}</h4>
                <p className="text-blue-100 text-sm">
                  {t('responseTime')}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-2xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {t('personalizedDemo')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{t('firstName')} *</Label>
                      <Input
                        id="firstName"
                        type="text"
                        placeholder={t('yourFirstName')}
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{t('lastName')} *</Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder={t('yourLastName')}
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('professionalEmail')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('yourEmail')}
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>

                  {/* Company and Role */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">{t('company')} *</Label>
                      <Input
                        id="company"
                        type="text"
                        placeholder={t('yourCompany')}
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">{t('role')}</Label>
                      <Input
                        id="role"
                        type="text"
                        placeholder={t('yourRole')}
                        value={formData.role}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        className="h-12"
                      />
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="space-y-2">
                    <Label htmlFor="budget">{t('monthlyBudget')} *</Label>
                    <Select 
                      value={formData.budget} 
                      onValueChange={(value) => handleInputChange('budget', value)}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder={t('selectBudget')} />
                      </SelectTrigger>
                      <SelectContent>
                        {getBudgetOptions().slice(1).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message">{t('message')}</Label>
                    <Textarea
                      id="message"
                      placeholder={t('projectDescription')}
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows={4}
                      className="resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={!isFormValid() || isSubmitting}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{t('sending')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Send className="w-5 h-5" />
                        <span>{t('sendRequest')}</span>
                      </div>
                    )}
                  </Button>

                  {/* Privacy Notice */}
                  <p className="text-xs text-gray-500 text-center">
                    {t('privacyNotice')}
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;